/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { Drawer, DrawerHeader, DrawerTitle } from '@src/components/ui/drawer';
import { Drawer as DrawerPrimitive } from 'vaul';
import Icon from '../coreUI/icon';
import Search from '@src/v3/assets/svg/search';
import { PropertyFilter } from '@src/v3/interfaces/utils/http';
import { AddressAutoSearch } from '@src/v3/interfaces/components/searchAutocomplete';
import { DEFAULT_FILTERS } from '@src/v3/constants/listingsFilter';
import { SORT_OPTIONS } from '@src/v3/constants/sort';
import { parseURLToPropertyFilter } from '@src/v3/utils/helper';
import { SORT_MAP } from '@src/v3/interfaces/app/propertyLists';
import { useSearchParams } from 'next/navigation';
import AmenitiesDropdown from '../listingsFilter/amenitiesDropdown';
import RoomsDropdown from '../listingsFilter/roomsDropdown';
import RangeFilterDropdown from '../listingsFilter/rangeFilterDropdown';
import SortDropdown from '../listingsFilter/sortDropdown';
import Button from '../coreUI/button';
import SearchAutocomplete from '../searchAutocomplete';

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  applyFilters?: (filters: PropertyFilter) => void;
  handleSearchChange?: (address: AddressAutoSearch[] | null) => void;
  resetFilters?: () => void;
  setIsMobileFilterActive?: (isMobileFilterActive: boolean) => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  applyFilters,
  handleSearchChange,
  resetFilters,
  setIsMobileFilterActive,
}) => {
  const searchParams = useSearchParams();
  const [filters, setFilters] = useState<PropertyFilter>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<PropertyFilter>(DEFAULT_FILTERS);
  const [defaultSearchValue, setDefaultSearchValue] = useState<
    AddressAutoSearch[]
  >([]);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Handle visual viewport changes (keyboard open/close) - iOS compatible
  useEffect(() => {
    if (!isOpen) return undefined;

    let timeoutId: NodeJS.Timeout | null = null;

    const updateHeight = () => {
      // Use visualViewport if available, otherwise window.innerHeight
      const height = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(height);
    };

    // Debounced update for iOS keyboard animation timing
    const debouncedUpdateHeight = () => {
      if (timeoutId) clearTimeout(timeoutId);
      // Small delay to let iOS keyboard animation complete
      timeoutId = setTimeout(() => {
        requestAnimationFrame(updateHeight);
      }, 100);
    };

    // Initial height
    updateHeight();

    // Listen to visual viewport changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', debouncedUpdateHeight);
      window.visualViewport.addEventListener('scroll', updateHeight);
    }
    window.addEventListener('resize', debouncedUpdateHeight);

    // iOS Safari: Also listen for focus events to detect keyboard close
    const handleFocusOut = () => {
      // Delay to let iOS recalculate viewport after keyboard closes
      setTimeout(updateHeight, 300);
    };
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          debouncedUpdateHeight
        );
        window.visualViewport.removeEventListener('scroll', updateHeight);
      }
      window.removeEventListener('resize', debouncedUpdateHeight);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [isOpen]);

  // Initialize filters from URL params (same as desktop version)
  useEffect(() => {
    if (searchParams) {
      const urlFilters = parseURLToPropertyFilter(searchParams);
      setFilters(urlFilters);
      setAppliedFilters(urlFilters);
    }
  }, [searchParams]);

  // Helper functions to check if filters are active (based on applied filters)
  const isPriceFilterActive = () => {
    return (
      appliedFilters.costMin !== DEFAULT_FILTERS.costMin ||
      appliedFilters.costMax !== DEFAULT_FILTERS.costMax
    );
  };

  const isRoomsFilterActive = () => {
    return (
      (appliedFilters.bedrooms?.length || 0) > 0 ||
      (appliedFilters.bathrooms?.length || 0) > 0
    );
  };

  const isAmenitiesFilterActive = () => {
    return appliedFilters.amenity && appliedFilters.amenity.length > 0;
  };

  const isSizeFilterActive = () => {
    return (
      appliedFilters.squareFootageMin !== DEFAULT_FILTERS.squareFootageMin ||
      appliedFilters.squareFootageMax !== DEFAULT_FILTERS.squareFootageMax
    );
  };

  const isSortFilterActive = () => {
    return appliedFilters.sortBy !== DEFAULT_FILTERS.sortBy;
  };

  useEffect(() => {
    if (
      isSizeFilterActive() ||
      isPriceFilterActive() ||
      isRoomsFilterActive() ||
      isAmenitiesFilterActive() ||
      isSortFilterActive()
    ) {
      setIsMobileFilterActive?.(true);
    } else {
      setIsMobileFilterActive?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const handleSearchSelect = (addresses: AddressAutoSearch[] | null) => {
    setDefaultSearchValue(addresses || []);
    if (handleSearchChange) {
      handleSearchChange(addresses);
    }
  };

  const handlePriceChange = (min: number, max: number) => {
    setFilters((prev) => ({
      ...prev,
      costMin: min ?? 0,
      costMax: max || 50000,
    }));
  };

  const handleBedsChange = (beds: string[]) => {
    setFilters((prev) => ({ ...prev, bedrooms: beds }));
  };

  const handleBathroomsChange = (bathrooms: string[]) => {
    setFilters((prev) => ({ ...prev, bathrooms: bathrooms }));
  };

  const handleAmenitiesChange = (amenities: string[]) => {
    setFilters((prev) => ({ ...prev, amenity: amenities }));
  };

  const handleApplyFilters = () => {
    if (applyFilters) {
      applyFilters(filters);
    }
    setAppliedFilters(filters);
    onClose();
  };

  const handleClose = () => {
    onClose();
    setFilters(appliedFilters);
  };

  const handleResetFilter = () => {
    setIsMobileFilterActive?.(false);
    resetFilters?.();
    onClose();
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={handleClose}
      modal={true}
      direction="bottom"
    >
      <DrawerPrimitive.Content
        ref={contentRef}
        className="fixed inset-x-0 top-0 z-[80] flex w-full flex-col bg-white"
        style={{
          border: 'none',
          outline: 'none',
          boxShadow: 'none',
          height: viewportHeight ? `${viewportHeight}px` : '100dvh',
        }}
      >
        {/* Header with Close Button */}
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="text-2xl font-medium text-gray-1000 tracking-[-0.24px]">
            Filters
          </DrawerTitle>
          <Button
            variant="ghost"
            onClick={handleClose}
            className="p-2 transition-colors duration-200"
            aria-label="Close filters"
          >
            <Icon
              iconName="crossicon"
              iconProps={{ className: 'w-5 h-5 text-gray-1000' }}
            />
          </Button>
        </DrawerHeader>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto pb-4">
          <div className="space-y-6">
            {/* Location Search
            <div className="p-4 pb-6 space-y-7 border-b-4 border-gray-200">
              <div className="flex flex-col">
                <span className="text-lg font-semibold text-gray-1000">
                  Location
                </span>
                <span className="text-base text-gray-800">
                  Where are you looking to live?
                </span>
              </div>
              <div className="relative">
                <div className="flex absolute left-5 top-0 z-[999] items-center h-full w-16 text-gray-dark/60">
                  <span className="flex justify-center items-center w-5 h-5 text-gray-900">
                    <Search />
                  </span>
                </div>
                <div className="relative z-[100]">
                  <SearchAutocomplete
                    defaultInputValue=""
                    defaultValue={defaultSearchValue}
                    searchType=""
                    onSelect={handleSearchSelect}
                  />
                </div>
              </div>
            </div> */}

            {/* Price Range using existing component */}
            <div className="p-4 pb-5 border-b-4 border-gray-200">
              <RangeFilterDropdown
                initialRange={{
                  min: filters.costMin ?? 0,
                  max: filters.costMax || 50000,
                }}
                onRangeChange={(range) =>
                  handlePriceChange(range.min, range.max)
                }
                minRange={0}
                maxRange={50000}
                title="Price"
                subTitle="Rental Prices, includes all fees and concessions"
                isCurrency={true}
                containerClassName="space-y-3"
              />
            </div>

            {/* Amenities using existing component */}
            <div className="p-4 pb-5 border-b-4 border-gray-200">
              <AmenitiesDropdown
                selectedAmenities={filters.amenity || []}
                onAmenitiesChange={handleAmenitiesChange}
                containerClassName="space-y-3"
              />
            </div>

            {/* Rooms using existing component */}
            <div className="p-4 pb-5 border-b-4 border-gray-200">
              <RoomsDropdown
                selectedBeds={filters.bedrooms}
                selectedBathrooms={filters.bathrooms}
                onBedsChange={handleBedsChange}
                onBathroomsChange={handleBathroomsChange}
                containerClassName="space-y-3"
              />
            </div>

            {/* Size Range - Mobile specific */}
            <div className="p-4 pb-5 space-y-3 border-b-4 border-gray-200">
              <RangeFilterDropdown
                initialRange={{
                  min: filters.squareFootageMin ?? 0,
                  max: filters.squareFootageMax || 5000,
                }}
                title="Size"
                minRange={0}
                maxRange={5000}
                isCurrency={false}
                unit="sqft"
                onRangeChange={(value) => {
                  setFilters({
                    ...filters,
                    squareFootageMin: value?.min ?? 0,
                    squareFootageMax: value?.max || 5000,
                  });
                }}
              />
            </div>

            {/* Sort using existing component */}
            <div className="p-4 space-y-3">
              <SortDropdown
                selectedSort={filters.sortLabel}
                onSortChange={(sortLabel) => {
                  const sortBy =
                    SORT_MAP[sortLabel as keyof typeof SORT_MAP] || '';

                  setFilters({
                    ...filters,
                    sortBy: sortBy,
                    sortLabel: sortLabel,
                  });
                }}
                containerClassName="space-y-3"
              />
            </div>
          </div>
        </div>

        {/* Sticky Footer - using flex instead of fixed to prevent keyboard issues */}
        <div className="flex-shrink-0 bg-white border-t border-gray-200 pt-4 px-5 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="flex gap-5">
            <Button
              variant="ghost"
              size="lg"
              className="text-sm font-medium p-0 hover:bg-transparent underline min-w-0"
              onClick={handleResetFilter}
            >
              Reset Filters
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleApplyFilters}
              // disabled={!hasAnyFilterChanged(filters)}
              className="flex-1 bg-focus rounded-xl text-base font-semibold transition-colors"
              // loading={isLoading}
            >
              Apply Filter
            </Button>
          </div>
        </div>
      </DrawerPrimitive.Content>
    </Drawer>
  );
};

export default FilterDrawer;
